import React, { useState, useEffect } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { translations } from '../../translations';

interface InvoiceHistoryProps {
  isDarkMode: boolean;
  language: string;
}

interface Invoice {
  id: string;
  amount_total: number;
  currency: string;
  created: number;
  invoice_pdf: string;
  status: string;
}

export function InvoiceHistory({ isDarkMode, language }: InvoiceHistoryProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-invoices`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.invoices) {
        throw new Error('Invalid response format');
      }

      setInvoices(data.invoices);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat(language === 'FR' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 text-red-500 text-sm">
        {error}
      </div>
    );
  }

  if (invoices.length === 0) {
    return null;
  }

  return (
    <div className={`rounded-lg border ${
      isDarkMode 
        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
        : 'bg-white border-[#e5e7eb]'
    }`}>
      <div className="p-4 border-b border-gray-200 dark:border-gray-800">
        <h3 className={`font-semibold ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>
          {t.invoice_history}
        </h3>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-800">
        {invoices.map((invoice) => (
          <div 
            key={invoice.id}
            className="flex items-center justify-between p-4"
          >
            <div>
              <div className={`font-medium ${
                isDarkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>
                {formatAmount(invoice.amount_total, invoice.currency)}
              </div>
              <div className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {formatDate(invoice.created)}
              </div>
            </div>
            <a
              href={invoice.invoice_pdf}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'hover:bg-[#2b2b2b] text-gray-400 hover:text-gray-300'
                  : 'hover:bg-gray-100 text-gray-600 hover:text-gray-700'
              }`}
            >
              <Download size={18} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}